/**
 * @fileoverview Session Templates Drawer Component
 * @created 10-10-2025 19:49:52
 * 
 * Drawer component for managing session templates from the Planner page.
 * Provides quick access to create/edit/delete global templates during scheduling workflow.
 */

import React, { useState } from 'react';
import {
  Drawer,
  Stack,
  Group,
  Button,
  Title,
  Text,
  TextInput,
  Alert,
  Box
} from '@mantine/core';
import {
  IconClock,
  IconPlus,
  IconSearch,
  IconInfoCircle
} from '@tabler/icons-react';
import { useSessionTemplates, useCreateSessionTemplate, useUpdateSessionTemplate, useDeleteSessionTemplate } from '../../hooks/api/useSessionTemplates';
import { SessionTemplatesList } from '../../features/shared/session-templates/SessionTemplatesList';
import { SessionTemplateForm } from '../../features/shared/session-templates/SessionTemplateForm';
import { notifications } from '@mantine/notifications';
import type { SessionTemplate } from '../../types/session-templates';

export interface SessionTemplatesDrawerProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * Session Templates Drawer Component
 * Quick access to template management from Planner
 */
export function SessionTemplatesDrawer({
  opened,
  onClose
}: SessionTemplatesDrawerProps): JSX.Element {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SessionTemplate | null>(null);

  // Queries and mutations
  const { data: templatesData, isLoading } = useSessionTemplates({ 
    search: searchTerm,
    isActive: true 
  });
  const createMutation = useCreateSessionTemplate();
  const updateMutation = useUpdateSessionTemplate();
  const deleteMutation = useDeleteSessionTemplate();

  /**
   * Handle creating new template
   */
  const handleCreate = () => {
    setSelectedTemplate(null);
    setShowForm(true);
  };

  /**
   * Handle editing template
   */
  const handleEdit = (template: SessionTemplate) => {
    setSelectedTemplate(template);
    setShowForm(true);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (data: any) => {
    try {
      if (selectedTemplate) {
        await updateMutation.mutateAsync({ id: selectedTemplate.id, data });
        notifications.show({
          title: 'Success',
          message: 'Session template updated successfully',
          color: 'green'
        });
      } else {
        await createMutation.mutateAsync(data);
        notifications.show({
          title: 'Success',
          message: 'Session template created successfully',
          color: 'green'
        });
      }
      setShowForm(false);
      setSelectedTemplate(null);
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to save template',
        color: 'red'
      });
    }
  };

  /**
   * Handle template deletion
   */
  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(id);
      notifications.show({
        title: 'Success',
        message: 'Session template deleted successfully',
        color: 'green'
      });
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to delete template',
        color: 'red'
      });
    }
  };

  const templates = templatesData?.data || [];

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        title={
          <Group gap="sm">
            <IconClock size={24} />
            <Title order={3}>Session Templates</Title>
          </Group>
        }
        position="right"
        size="xl"
        padding="lg"
      >
        <Stack gap="lg">
          {/* Description */}
          <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
            <Text size="sm">
              Global session time templates that can be reused across multiple rooms.
              Create templates here, then assign them to rooms during room setup.
            </Text>
          </Alert>

          {/* Search and Create */}
          <Group gap="md">
            <TextInput
              placeholder="Search templates..."
              leftSection={<IconSearch size={16} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleCreate}
            >
              Create Template
            </Button>
          </Group>

          {/* Templates List */}
          <Box>
            <SessionTemplatesList
              templates={templates}
              onEdit={handleEdit}
              onDelete={handleDelete}
              loading={isLoading}
              compact
            />
          </Box>
        </Stack>
      </Drawer>

      {/* Template Form */}
      {showForm && (
        <SessionTemplateForm
          template={selectedTemplate}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowForm(false);
            setSelectedTemplate(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </>
  );
}

